
import { defineComponent, onBeforeUnmount, onMounted, ref } from 'vue'
import { Help } from '@icon-park/vue-next'
import { useWorkspaceStore } from '@/store/workspace'
import { resolveApiUrl } from '@/services/api'
import type { PptTaskArtifact, PptTaskRecord } from '@/services/tasks'

export default defineComponent({
  name: 'TaskCenter',
  components: { Help },
  setup() {
    const store = useWorkspaceStore()
    const nowTick = ref(Date.now())
    const downloadingTaskId = ref('')
    let durationTimer: ReturnType<typeof setInterval> | null = null

    const formatStatus = (status: string) => {
      const map: Record<string, string> = {
        queued: '排队中', running: '处理中', completed: '已完成',
        outline_ready: '大纲完成', sample_ready: '样张完成',
        failed: '失败', cancelled: '已取消', expired: '已过期',
      }
      return map[status] || status
    }
    const taskView = (task: PptTaskRecord) => store.activeTask?.task.id === task.id ? { ...task, ...store.activeTask.task } : task
    const isProcessing = (task: PptTaskRecord) => ['queued', 'running', 'outline_ready', 'sample_ready'].includes(task.status)
    const isDownloadable = (task: PptTaskRecord) => task.status === 'completed'
    const progressPercent = (task: PptTaskRecord) => Math.max(0, Math.min(100, Math.round(task.progress || 0)))
    const spentPoints = (task: PptTaskRecord) => Math.max((task.cost_points || 0) - (task.refunded_pages || 0), 0)
    const parseTime = (value?: string | null) => value ? new Date(value).getTime() : 0
    const taskDurationMs = (task: PptTaskRecord) => {
      const startedAt = parseTime(task.created_at)
      if (!startedAt) return 0
      const finishedAt = parseTime(task.completed_at) || nowTick.value
      return Math.max(finishedAt - startedAt, 0)
    }
    const formatDuration = (ms: number) => {
      if (ms < 60 * 1000) return '不足 1 分钟'
      const minutes = Math.round(ms / 60000)
      if (minutes < 60) return `${minutes} 分钟`
      const hours = Math.floor(minutes / 60)
      const rest = minutes % 60
      return rest ? `${hours} 小时 ${rest} 分钟` : `${hours} 小时`
    }
    const formatDateTime = (value?: string | null) => {
      if (!value) return '暂无'
      return new Date(value).toLocaleString('zh-CN', {
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
    }

    const qualityFailureText = (task: PptTaskRecord) => {
      const summary = task.quality_failure_summary || []
      if (!summary.length) return ''
      return `质量验收失败：${summary.map(item => `P${item.page_index + 1} ${item.reason}`).join('；')}`
    }

    const downloadArtifacts = (task: PptTaskRecord) => {
      if (store.activeTask?.task.id !== task.id) return []
      return store.activeTask.artifacts.filter(artifact =>
        (artifact.type === 'pptx' || artifact.artifact_type === 'pptx') && !!artifact.url)
    }
    const firstDownloadArtifact = (taskId: string) => {
      if (store.activeTask?.task.id !== taskId) return null
      return store.activeTask.artifacts.find(artifact =>
        (artifact.type === 'pptx' || artifact.artifact_type === 'pptx') && !!artifact.url) || null
    }
    const downloadArtifact = async (artifact: PptTaskArtifact, taskId = '') => {
      const raw = artifact.url || ''
      if (!raw) return
      if (taskId) downloadingTaskId.value = taskId
      const url = resolveApiUrl(raw)
      try {
        const token = localStorage.getItem('ppt_token')
        // presigned 绝对 URL（OSS）自带鉴权签名，向第三方域发送 JWT 属凭据泄露；
        // 仅相对 /api/files/ 路径（同源代理）携带 Authorization。
        const presigned = /^https?:\/\//i.test(raw)
        const headers: Record<string, string> = !presigned && token ? { Authorization: `Bearer ${token}` } : {}
        const res = await fetch(url, { headers })
        if (!res.ok) throw new Error(`HTTP ${res.status}`)
        const blob = await res.blob()
        const objectUrl = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = objectUrl
        a.download = raw.split('/').pop() || 'ppt.pptx'
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(objectUrl)
      }
      catch (e) {
        store.error = e instanceof Error ? e.message : '下载失败'
      }
      finally {
        if (taskId && downloadingTaskId.value === taskId) downloadingTaskId.value = ''
      }
    }
    const downloadTaskPptx = async (task: PptTaskRecord) => {
      downloadingTaskId.value = task.id
      try {
        let artifact = firstDownloadArtifact(task.id)
        if (!artifact) {
          await store.selectTask(task.id)
          if (store.activeTask?.task.id !== task.id) return
          artifact = firstDownloadArtifact(task.id)
        }
        if (!artifact) {
          store.error = '未找到 PPTX 下载文件'
          return
        }
        await downloadArtifact(artifact)
      }
      finally {
        if (downloadingTaskId.value === task.id) downloadingTaskId.value = ''
      }
    }

    onMounted(() => {
      durationTimer = setInterval(() => {
        nowTick.value = Date.now()
      }, 30000)
    })
    onBeforeUnmount(() => {
      if (durationTimer) clearInterval(durationTimer)
    })

    return {
      store, formatStatus, taskView, isProcessing, isDownloadable,
      progressPercent, spentPoints, taskDurationMs, formatDuration, formatDateTime,
      qualityFailureText, downloadArtifacts, downloadArtifact, downloadTaskPptx, downloadingTaskId,
    }
  },
})
